import { AsyncPipe, NgClass } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { RootReducer, Store } from '@app/app.reducers';
import { getProductCount } from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { getMyProducts } from '@app/checkout/modules/checkout-shared/reducers/persisted-list.reducer';
import { StorePersistService } from '@app/checkout/modules/checkout-shared/services/store-persist.service';
import { LoginDialogComponent } from '@app/dialog/components/login-dialog/login-dialog.component';
import { DialogService } from '@app/dialog/services/dialog.service';
import { IconComponent } from '@app/shared/components/icon/icon.component';
import { getStorefront } from '@app/shared/reducers/storefront.reducer';
import { CognitoService } from '@app/shared/services/cognito.service';
import { getCxmlToken } from '@app/user/reducers/auth.reducer';
import {
	getIsUserChecked,
	getUser,
	getUserPermissions,
	isLoggedUser,
} from '@app/user/reducers/user.reducer';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCartShopping } from '@fortawesome/pro-solid-svg-icons/faCartShopping';
import { faPersonDolly } from '@fortawesome/pro-solid-svg-icons/faPersonDolly';
import { faPersonDollyEmpty } from '@fortawesome/pro-solid-svg-icons/faPersonDollyEmpty';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { pluckSuccessData } from '@granodigital/grano-remote-data';
import { UntilDestroy } from '@ngneat/until-destroy';
import { environment } from '@src/environments/environment';
import { combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

/** Navigation actions component */
@UntilDestroy()
@Component({
	imports: [AsyncPipe, NgClass, IconComponent, RouterLink, MatMenuModule, MatButton],
	selector: 'g-nav-actions',
	templateUrl: './nav-actions.component.html',
	styleUrls: ['./nav-actions.component.scss'],
})
export class NavActionsComponent {
	readonly cognito = inject(CognitoService);
	private readonly store = inject<Store<RootReducer.State>>(Store);
	private readonly storePersistService = inject(StorePersistService);
	private readonly dialogService = inject(DialogService);
	private readonly router = inject(Router);

	/**
	 * Mode of the component
	 * - full: User actions and cart icon
	 * - user: Only user actions
	 * - cart: Only cart icon
	 * - checkout: Only user icon with dropdown (no name, no cart)
	 */
	@Input() readonly mode: 'full' | 'user' | 'cart' | 'checkout' = 'full';
	readonly icons = { faUser, faCartShopping, faPersonDollyEmpty, faPersonDolly, faCaretDown };
	readonly productCount$ = this.store.select(getProductCount);
	readonly myProductsCount$ = this.store.select(getMyProducts).pipe(
		pluckSuccessData,
		map((list) => list.products.length),
	);
	readonly isPunchout$ = this.store.select(getCxmlToken).pipe(map((token) => !!token));
	readonly isNewLayout = this.storePersistService.getFlag('enableNewLayout');
	readonly hasGonePastCart$ = this.router.events.pipe(
		filter((event): event is NavigationEnd => event instanceof NavigationEnd),
		map((event) => event.url),
		startWith(this.router.routerState.snapshot.url),
		map((url) => url.includes('/checkout/v2/') && !url.includes('/checkout/v2/cart')),
	);
	readonly cartRoute$ = this.hasGonePastCart$.pipe(
		map((hasGonePastCart) =>
			hasGonePastCart || this.isNewLayout ? ['/checkout/v2/cart'] : ['/checkout/cart'],
		),
	);
	readonly userPermissions$ = this.store.select(getUserPermissions);
	readonly isUserLoaded$ = this.store.select(getIsUserChecked);
	readonly user$ = this.store
		.select(getUser)
		.pipe(map((user) => (isLoggedUser(user) ? user : undefined)));
	readonly isApprovalAdmin$ = this.user$.pipe(
		map((user) => user?.permissions?.access_approval_workflow_products ?? false),
	);
	readonly adminUrl$ = combineLatest([
		this.store.select(getUser),
		this.store.select(getStorefront),
	]).pipe(
		map(([user, store]) => {
			if (!(user?.isSuperAdmin || user?.permissions?.access_storefront_admin)) return undefined;

			return environment.adminUrl
				.replaceAll('{{CUSTOMER_ID}}', `${store?.customer_id}`)
				.replaceAll('{{STORE_ID}}', `${store?.id}`)
				.replaceAll('{{BASE_URL}}', `${store?.base_url}`);
		}),
	);
	readonly openLoginDialog = (): void => {
		this.dialogService.open(LoginDialogComponent);
	};
}
